import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { RequestError, Service } from 'webapp-types';
import { PromotionIdentifier, type BillingFeature } from 'webapp/enums/enums';

import SubmitButtonComponent from 'webapp/components/submit-button';

export interface Args {
	feature: BillingFeature;
	isSubmitInProgress: boolean;
	size?: string;
	onTrigger?: () => void;
}

export default class RequestTrialAddonButtonComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;

	@tracked isRequestTrialAddonInProgress = false;

	get isLoading() {
		return this.isRequestTrialAddonInProgress || this.args.isSubmitInProgress;
	}

	get feature() {
		return this.args.feature;
	}

	get text() {
		const promotion = this.flowPromotionsService.getOptionalTrialAddonPromotion(this.feature);
		return promotion?.description || 'Try for free';
	}

	@action
	requestTrialAddon() {
		this.isRequestTrialAddonInProgress = true;

		this.flowPromotionsService
			.startPromotion({
				promotionId: PromotionIdentifier.TRIAL_ADDON,
				productId: this.feature,
			})
			.then(
				() => {
					this.infolayerService.ok('Addon trial started!');
				},
				(errorData: RequestError) => {
					this.requestService.handleGenericThrownError(errorData);
				},
			)
			.finally(() => {
				this.isRequestTrialAddonInProgress = false;

				if (this.args.onTrigger) {
					this.args.onTrigger();
				}
			});
	}
	<template>
		<SubmitButtonComponent
			@text={{this.text}}
			@extraClass="request-trial-addon-button"
			@size={{@size}}
			@submitInProgress={{this.isRequestTrialAddonInProgress}}
			@onTrigger={{this.requestTrialAddon}}
		/>
	</template>
}
